import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button, Modal, Checkbox, notification, Spin } from "antd";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import request from "helper/request";
import paymentMethodImg from "./paymentmethodimg.webp";
import PaymentForm from "./PaymentForm";
import SecuredCardIcon from "./cardIcon.webp";

const stripePromise = loadStripe(window.REACT_APP_STRIPE_PK);

const PaymentMethodStep1 = ({ handleCheckbox, isChecked }) => {
  return (
    <div>
      <h2
        style={{
          width: "100%",
          textAlign: "center",
          marginBottom: "20px",
          fontSize: "20px",
          fontWeight: "bold"
        }}
      >
        You are one step away!
      </h2>
      <div
        id="text"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <img
          src={paymentMethodImg}
          alt="payment method flexio"
          style={{ width: "50%" }}
        />
        <div style={{ width: "50%", padding: "20px" }}>
          <p>
            Before we can complete your Flexio Pool order, we need to add your payment
            method, you won&apos;t be charged until we pick up your package.
          </p>
          <div>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <Checkbox checked={isChecked} onChange={handleCheckbox} />
            </span>
            <span style={{ marginLeft: "8px" }}>
              I understand that no charges will be made until Flexio successfully picks up
              my package.
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px"
        }}
      ></div>
    </div>
  );
};

const PaymentMethodStep2 = ({
  clientSecret,
  paymentFormRef,
  handleNextStep,
  setOpenPaymentMethodModal,
  isLoading,
  setIsLoading,
  setFormSubmitted,
  onSuccess
}) => {
  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <img
          src={SecuredCardIcon}
          alt="Payment Icon"
          style={{ width: "30px", height: "auto", marginRight: "10px" }}
        />
        <h2
          style={{
            width: "100%",
            textAlign: "center",
            marginBottom: "20px",
            fontSize: "20px",
            fontWeight: "bold",
            color: "#1F3998"
          }}
        >
          Payment Information
        </h2>
      </div>

      <Spin size="large" spinning={isLoading} style={{}}>
        <Elements stripe={stripePromise}>
          {clientSecret ? (
            <PaymentForm
              clientSecret={clientSecret}
              paymentFormRef={paymentFormRef}
              handleNextStep={handleNextStep}
              setOpenPaymentMethodModal={setOpenPaymentMethodModal}
              setIsLoading={setIsLoading}
              setFormSubmitted={setFormSubmitted}
              onSuccess={onSuccess}
            />
          ) : (
            <p>Loading...</p>
          )}
        </Elements>
      </Spin>
    </div>
  );
};

function Payment({ openPaymentMethodModal, setOpenPaymentMethodModal, onSuccess }) {
  const [clientSecret, setClientSecret] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const paymentFormRef = useRef();

  const handleExternalFormSubmit = useCallback(() => {
    if (paymentFormRef.current) {
      paymentFormRef.current.requestSubmit();
    }
  }, []);

  const fetchClientSecret = useCallback(async () => {
    try {
      const res = await request.get("/payment-method/request-intent");
      setClientSecret(res.clientSecret);
    } catch (error) {
      notification.error({
        message:
          "Card form unable to open at this moment. Please try again later or contact support."
      });
      setOpenPaymentMethodModal(false);
    }
  }, [setOpenPaymentMethodModal]);

  // Auto-fetch client secret when modal opens
  useEffect(() => {
    if (openPaymentMethodModal) {
      fetchClientSecret();
    }
  }, [openPaymentMethodModal, fetchClientSecret]);

  const modalFooter = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "10px"
        }}
      >
        <Button
          className="KeepItBlue"
          onClick={handleExternalFormSubmit}
          disabled={formSubmitted}
          type="submit"
          form="PaymentForm"
        >
          Add Payment Method
        </Button>
      </div>
    ),
    [handleExternalFormSubmit, formSubmitted]
  );

  const handleModalCancel = useCallback(() => {
    setOpenPaymentMethodModal(false);
    setClientSecret("");
    setFormSubmitted(false);
    setIsLoading(false);
  }, [setOpenPaymentMethodModal]);

  return (
    <Modal
      visible={openPaymentMethodModal}
      onCancel={handleModalCancel}
      footer={modalFooter}
      maxWidth={700}
      width={700}
      centered
    >
      <div
        style={{
          padding: "30px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px"
        }}
      >
        <PaymentMethodStep2
          clientSecret={clientSecret}
          paymentFormRef={paymentFormRef}
          setOpenPaymentMethodModal={setOpenPaymentMethodModal}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setFormSubmitted={setFormSubmitted}
          onSuccess={onSuccess}
        />
      </div>
    </Modal>
  );
}

export default Payment;
